import { type CSSProperties, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * IntegrationCommonUsagePanel — §6.8 공통 모듈 시연 패널
 * C058 Feign · C059 S3 · C060 SFTP · C009 FileManager SPI 재사용 현황 표시
 */
export function IntegrationCommonUsagePanel() {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const usages = [
    {
      module: '★ eraf-integration-http (C058)',
      api: '@FeignClient + @EnableFeignClients (biz opt-in)',
      role: '외부 연동 선언적 호출',
    },
    {
      module: 'eraf-integration-http (C058)',
      api: 'FeignSupportAutoConfiguration',
      role: 'timeout/retry 자동 적용 (biz 0줄)',
    },
    {
      module: 'eraf-integration-http (C058)',
      api: 'FeignTraceInterceptor',
      role: 'X-Trace-Id 전파 → 이력 traceId',
    },
    {
      module: 'eraf-integration-http (C058)',
      api: 'ErafFeignErrorDecoder',
      role: '비-2xx → HttpClientException → INTGHUB-502-001',
    },
    {
      module: '★ eraf-integration-s3 (C059)',
      api: 'S3FileManager implements FileManager',
      role: 'backend=s3 시 자동 주입 SPI · presign 지원',
    },
    {
      module: 'eraf-integration-s3 (C059)',
      api: 'S3Properties (eraf.integration.s3.*)',
      role: 'endpoint/pathStyleAccess/presign TTL 설정 표시',
    },
    {
      module: '★ eraf-integration-ftp (C060)',
      api: 'SftpFileManager implements FileManager',
      role: 'backend=sftp 시 자동 주입 · presign 미지원',
    },
    {
      module: 'eraf-integration-ftp (C060)',
      api: 'SftpProperties (eraf.integration.sftp.*)',
      role: 'host/port/username/basePath 설정 표시 (시크릿 마스킹)',
    },
    {
      module: '★ eraf-core (C009)',
      api: 'FileManager.upload/download/presignDownload/backend()',
      role: 'backend 무관 동일 SPI 시연 (코드 변경 0)',
    },
    {
      module: 'eraf-core (C009)',
      api: 'UploadRequest · StoredFile · DownloadResource',
      role: '파일 DTO — backend 무관',
    },
    {
      module: 'eraf-core (C009)',
      api: 'FileErrorCode.FILE_PRESIGN_UNSUPPORTED',
      role: 'presign 미지원 → INTGHUB-501-001 wrap',
    },
    {
      module: 'eraf-core (C017)',
      api: 'ErafHttpClients.restClient()',
      role: 'ad-hoc 동적 URL ping',
    },
    {
      module: 'eraf-core (C017)',
      api: 'HttpClientException',
      role: '다운스트림 오류 정규화',
    },
    {
      module: 'eraf-core (C002)',
      api: 'ApiResponse.page(...)',
      role: '모든 목록 표준 응답',
    },
    {
      module: 'eraf-core (C014)',
      api: '@Audited(action="INTEGRATION_INVOKED")',
      role: '호출 자기 감사 (audit-log 소비)',
    },
    {
      module: 'eraf-core (C006)',
      api: 'RequestContext.userId()/traceId()',
      role: '이력 actor·traceId 기록',
    },
    {
      module: 'eraf-data-jpa (C042)',
      api: 'GenericRepository.findAllActive',
      role: '이력 페이지 조회',
    },
    {
      module: 'eraf-data-jpa (C041)',
      api: 'SpecificationBuilder + SortWhitelist',
      role: '이력 동적 검색 + SQL 인젝션 차단',
    },
    {
      module: 'eraf-security (C030)',
      api: '@HasPermission("INTEGRATION_INVOKE")',
      role: '메서드 권한 이중 방어',
    },
    {
      module: 'eraf-security (C029)',
      api: 'SecurityChainCustomizer + PathPolicy',
      role: '/integration-hub/** ROLE_ADMIN 1차 게이트',
    },
    {
      module: 'eraf-security (C031)',
      api: 'SecurityAuditLogger',
      role: '권한 거부 자동 보안 감사',
    },
    {
      module: 'eraf-swagger (C037)',
      api: '@ApiSuccessResponse',
      role: '9 endpoint OpenAPI 문서화',
    },
    {
      module: '★ eraf-integration-tcp (C061)',
      api: 'TcpClient.sendReceive(byte[]) (BlockingTcpClient)',
      role: 'TCP echo 왕복 — connect/write/read 타이밍 시연',
    },
    {
      module: 'eraf-integration-tcp (C061)',
      api: 'TcpServer.isRunning() / boundPort()',
      role: '콘솔 상태(러닝/포트) 조회',
    },
    {
      module: 'eraf-integration-tcp (C061)',
      api: 'LengthPrefixCodec (4B BE)',
      role: 'hex 뷰의 프레임 prefix 근거',
    },
    {
      module: '★ eraf-integration-grpc (C062)',
      api: 'GrpcChannelFactory.channel(name) + EchoServiceGrpc stub',
      role: 'unary/streaming echo 호출',
    },
    {
      module: 'eraf-integration-grpc (C062)',
      api: 'HealthStatusManager (gRPC 표준 Health)',
      role: 'SERVING/NOT_SERVING 조회',
    },
    {
      module: 'eraf-integration-grpc (C062)',
      api: 'GrpcServerLifecycle.isRunning() / boundPort()',
      role: '콘솔 상태(러닝/포트) 조회',
    },
  ];

  return (
    <div
      style={{
        marginTop: 'var(--spacing-6)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls="common-usage-panel"
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--spacing-3) var(--spacing-4)',
          background: 'var(--color-surface-muted)',
          border: 'none',
          cursor: 'pointer',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          color: 'var(--color-text-secondary)',
          fontFamily: 'var(--font-family-sans)',
        }}
      >
        <span>{expanded ? '▾' : '▸'} {t('integrationHub.commonUsage.title')}</span>
        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 400 }}>
          C058 Feign · C059 S3 · C060 SFTP · C009 FileManager SPI · C061 TCP · C062 gRPC
        </span>
      </button>
      {expanded && (
        <div
          id="common-usage-panel"
          style={{ overflowX: 'auto' }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 'var(--font-size-xs)',
            }}
          >
            <thead>
              <tr style={{ background: 'var(--color-surface-muted)' }}>
                <th style={thStyle}>{t('integrationHub.commonUsage.col.module')}</th>
                <th style={thStyle}>{t('integrationHub.commonUsage.col.api')}</th>
                <th style={thStyle}>{t('integrationHub.commonUsage.col.role')}</th>
              </tr>
            </thead>
            <tbody>
              {usages.map((u, i) => (
                <tr key={i}>
                  <td style={tdStyle}>
                    <code style={{ fontFamily: 'var(--font-family-mono)' }}>{u.module}</code>
                  </td>
                  <td style={tdStyle}>
                    <code style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>
                      {u.api}
                    </code>
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--color-text-secondary)' }}>{u.role}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const thStyle: CSSProperties = {
  textAlign: 'left',
  padding: 'var(--spacing-2) var(--spacing-3)',
  borderBottom: '1px solid var(--color-surface-border)',
  color: 'var(--color-text-secondary)',
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

const tdStyle: CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  borderBottom: '1px solid var(--color-surface-border)',
  verticalAlign: 'top',
  color: 'var(--color-text-primary)',
};
